/**
 * tool-rating.ts — public "Rate this tool" widget + right-rail disclosure for
 * the public tool page (NewToolPublicPage.cshtml).
 *
 * Rating model (see the approved plan + ToolRatingApiController):
 *   - ANY visitor (incl. anonymous) can tap 1–5 stars; the score always counts.
 *   - Only SIGNED-IN users may attach an optional one-line comment (≤140 chars).
 *     The comment field is revealed only when window.__TN_AUTH__.isAuthenticated.
 *   - 1–2★ signed-in comments are HELD server-side for moderation; the score
 *     still counts immediately. The API reports this via { commentHeld: true }.
 *
 * POST goes through api.post(), which auto-attaches the RequestVerificationToken
 * header from the hidden antiforgery input the view renders for all visitors.
 *
 * The right rail is a disclosure, not a hover affordance: applyRailState()
 * below is the only writer of the open class, the content's `hidden`
 * attribute and `aria-expanded`, and Escape closes it.
 */
import { byId, query, queryAll } from "../types/dom.js";
import { api } from "../utils/api.js";
import { toast } from "../components/toast.js";
import { currentCulture } from "../localization/locale-runtime.js";
import { trackPersonalizationAction } from "./shared-tracking.js";
import { devLog } from "./dev-utils.js";
import { setIcon } from "./icon-svg.js";
import { AppState } from "./state.js";
const MAX_COMMENT_LENGTH = 140;
// Mirror NewToolPublicPage.cshtml `RatingSchemaThreshold` and
// ToolStructuredDataService.MinRatingsForSchema: the aggregate ("4.6 / 5 · N
// ratings") is only shown once there are enough ratings to be meaningful.
// Below the floor we keep the "Be the first to rate" empty state, so an
// optimistic client update can't disagree with what the server re-renders on
// the next load.
const MIN_RATINGS_FOR_SUMMARY = 5;
function t(key, fallback) {
    const translate = window.tnT;
    return typeof translate === "function" ? translate(key, fallback) : fallback;
}
export function formatRatingCount(count, culture = currentCulture()) {
    const category = new Intl.PluralRules(culture).select(count);
    const fallback = category === "one" ? "{0} rating" : "{0} ratings";
    return t(`js.toolrating.ratingCount.${category}`, fallback)
        .replace("{0}", new Intl.NumberFormat(culture).format(count));
}
export function formatRatingAverage(average, culture = currentCulture()) {
    return new Intl.NumberFormat(culture, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    }).format(average);
}
function isAuthenticated() {
    const auth = window
        .__TN_AUTH__;
    return auth?.isAuthenticated === true;
}
// ── Rating widget ────────────────────────────────────────────────────────────
function paintStars(stars, value) {
    stars.forEach((star) => {
        const starValue = Number(star.dataset["tnRatingStar"] ?? "0");
        const on = starValue <= value;
        star.classList.toggle("is-selected", on);
        star.setAttribute("aria-checked", on ? "true" : "false");
        const icon = query(".material-symbols-outlined", star);
        if (icon !== null) {
            setIcon(icon, on ? "star" : "star_border");
        }
    });
}
function reflectSummary(root, response) {
    const summary = query("[data-tn-rating-summary]");
    if (summary === null)
        return;
    const averageEl = query("[data-tn-rating-average]", summary);
    const countEl = query("[data-tn-rating-count]", summary);
    const countSuffixEl = query("[data-tn-rating-count-suffix]", summary);
    // Once we have enough ratings, drop the "Be the first to rate" empty state.
    const emptyEl = query("[data-tn-rating-empty]", summary);
    if (emptyEl !== null && response.count >= MIN_RATINGS_FOR_SUMMARY) {
        summary.textContent = "";
        const star = document.createElement("span");
        star.className = "material-symbols-outlined tool-meta-strip__star";
        star.setAttribute("aria-hidden", "true");
        setIcon(star, "star");
        const avg = document.createElement("span");
        avg.dataset["tnRatingAverage"] = "";
        avg.textContent = formatRatingAverage(response.average);
        const sep = document.createElement("span");
        sep.className = "tool-meta-strip__rating-sep";
        sep.textContent = " / 5 · ";
        const count = document.createElement("span");
        count.dataset["tnRatingCount"] = "";
        count.textContent = formatRatingCount(response.count);
        summary.append(star, avg, sep, count);
        return;
    }
    if (averageEl !== null)
        averageEl.textContent = formatRatingAverage(response.average);
    if (countEl !== null)
        countEl.textContent = formatRatingCount(response.count);
    if (countSuffixEl !== null)
        countSuffixEl.textContent = "";
}
function reflectTrigger(root, value) {
    const trigger = query("[data-tn-rating-trigger]", root);
    if (trigger === null)
        return;
    const icon = query(".material-symbols-outlined", trigger);
    if (icon !== null) {
        setIcon(icon, value > 0 ? "star" : "star_border");
    }
}
function getPanelFocusable(panel) {
    const nodes = Array.from(panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'));
    return nodes.filter((node) => {
        if (node.hidden)
            return false;
        const disabled = node.disabled;
        if (disabled)
            return false;
        const style = window.getComputedStyle(node);
        return style.display !== "none" && style.visibility !== "hidden";
    });
}
function trapPanelFocus(panel, event) {
    const focusable = getPanelFocusable(panel);
    if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
        event.preventDefault();
        last?.focus();
        return;
    }
    if (!event.shiftKey && active === last) {
        event.preventDefault();
        first?.focus();
    }
}
function openPanel(root, panel, trigger) {
    panel.hidden = false;
    trigger.setAttribute("aria-expanded", "true");
    // Reveal the comment field only for signed-in users; anon = stars only.
    const commentWrap = query("[data-tn-rating-comment-wrap]", root);
    const signinHint = query("[data-tn-rating-signin-hint]", root);
    if (isAuthenticated()) {
        if (commentWrap !== null)
            commentWrap.hidden = false;
        if (signinHint !== null)
            signinHint.hidden = true;
    }
    else {
        if (commentWrap !== null)
            commentWrap.hidden = true;
        if (signinHint !== null)
            signinHint.hidden = false;
    }
    // Move focus to the first focusable inside the dialog so AT users land
    // inside the modal rather than outside it.
    const focusable = getPanelFocusable(panel);
    if (focusable.length > 0) {
        focusable[0]?.focus();
    }
    else {
        if (!panel.hasAttribute("tabindex"))
            panel.setAttribute("tabindex", "-1");
        panel.focus();
    }
}
function closePanel(panel, trigger) {
    panel.hidden = true;
    trigger.setAttribute("aria-expanded", "false");
}
async function submit(root, stars) {
    const slug = root.dataset["toolSlug"];
    if (slug === undefined || slug.trim() === "")
        return;
    if (stars < 1 || stars > 5)
        return;
    let comment;
    if (isAuthenticated()) {
        const input = query("[data-tn-rating-comment]", root);
        const raw = input?.value.trim() ?? "";
        if (raw.length > 0) {
            comment = raw.slice(0, MAX_COMMENT_LENGTH);
        }
    }
    try {
        const response = await api.post("/api/ratings/submit", {
            slug,
            stars,
            comment,
        });
        root.dataset["userRated"] = String(response.yourScore);
        reflectSummary(root, response);
        reflectTrigger(root, response.yourScore);
        if (response.commentHeld) {
            toast.show({
                message: t("js.toolrating.commentHeld", "Thanks! Your rating counts. Your comment is awaiting review."),
                type: "info",
            });
        }
        else {
            toast.show({ message: t("js.toolrating.thanks", "Thanks for rating!"), type: "success" });
        }
        trackPersonalizationAction({
            toolSlug: slug,
            interactionType: "rating",
            action: "submit",
            source: "tool-rating-widget",
            metadata: { stars },
        });
    }
    catch {
        // api.post() already surfaced an error toast; revert optimistic fill.
        const prior = Number(root.dataset["userRated"] ?? "0");
        const starButtons = queryAll("[data-tn-rating-star]", root);
        paintStars(starButtons, Number.isFinite(prior) ? prior : 0);
    }
}
function initRatingWidget() {
    const root = query("[data-tn-rating]");
    if (root === null)
        return;
    if (root.dataset["tnRatingBound"] === "1")
        return;
    root.dataset["tnRatingBound"] = "1";
    const trigger = query("[data-tn-rating-trigger]", root);
    const panel = query("[data-tn-rating-panel]", root);
    const starButtons = queryAll("[data-tn-rating-star]", root);
    if (trigger === null || panel === null || starButtons.length === 0)
        return;
    const initial = Number(root.dataset["userRated"] ?? "");
    let currentValue = Number.isFinite(initial) && initial >= 1 ? initial : 0;
    paintStars(starButtons, currentValue);
    const initialAverage = query("[data-tn-rating-average]", root);
    if (initialAverage !== null) {
        const average = Number(initialAverage.textContent?.trim() ?? "0");
        if (Number.isFinite(average))
            initialAverage.textContent = formatRatingAverage(average);
    }
    const initialCount = query("[data-tn-rating-count]", root);
    const initialSuffix = query("[data-tn-rating-count-suffix]", root);
    if (initialCount !== null) {
        const count = Number(initialCount.textContent?.trim() ?? "0");
        if (Number.isFinite(count))
            initialCount.textContent = formatRatingCount(count);
        if (initialSuffix !== null)
            initialSuffix.textContent = "";
    }
    trigger.addEventListener("click", () => {
        if (panel.hidden) {
            openPanel(root, panel, trigger);
        }
        else {
            closePanel(panel, trigger);
        }
    });
    // Hover preview without committing.
    starButtons.forEach((star) => {
        const value = Number(star.dataset["tnRatingStar"] ?? "0");
        star.addEventListener("mouseenter", () => paintStars(starButtons, value));
        star.addEventListener("focus", () => paintStars(starButtons, value));
        star.addEventListener("click", () => {
            currentValue = value;
            paintStars(starButtons, value);
            void submit(root, value);
        });
        // Keyboard: Enter/Space activates the focused star (WCAG 2.1.1).
        star.addEventListener("keydown", (event) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                currentValue = value;
                paintStars(starButtons, value);
                void submit(root, value);
            }
        });
    });
    // Restore the committed value when the pointer leaves the star row.
    const starRow = query("[data-tn-rating-stars]", root);
    starRow?.addEventListener("mouseleave", () => paintStars(starButtons, currentValue));
    // Dismiss the panel on outside click / Escape.
    document.addEventListener("click", (event) => {
        if (panel.hidden)
            return;
        if (!root.contains(event.target)) {
            closePanel(panel, trigger);
        }
    });
    document.addEventListener("keydown", (event) => {
        if (event.key !== "Escape")
            return;
        if (!panel.hidden) {
            closePanel(panel, trigger);
            trigger.focus();
            return;
        }
        // The rail is this page's other dismissible surface. One handler owns
        // Escape for both so neither can swallow the key from the other, and the
        // rail half no-ops unless the rail is actually open.
        closeRail?.();
    });
    // Trap Tab/Shift+Tab inside the open dialog so keyboard users can't escape
    // into the page behind a modal. Mirrors command-palette.js trapFocus.
    panel.addEventListener("keydown", (event) => {
        if (event.key !== "Tab" || panel.hidden)
            return;
        trapPanelFocus(panel, event);
    });
    devLog("tool-rating", "Rating widget initialised", { currentValue });
}
// ── Right rail: an explicit disclosure ───────────────────────────────────────
/** Held open by an explicit action — tap, click or Enter on a rail control. */
const RAIL_OPEN_CLASS = "is-pinned-open";
/**
 * Closes the rail if it is open. Assigned by initRail() and called by the
 * document-level Escape handler in initRatingWidget(), which is registered
 * first and must not swallow the key from the rating dialog.
 */
let closeRail = null;
function applyRailState(rail, toggle, content, open) {
    rail.classList.toggle(RAIL_OPEN_CLASS, open);
    // The content panel ships `hidden` (NewToolPublicPage.cshtml) and this is its
    // only owner. `hidden` rather than a class because the resting rail is 48px
    // wide with `min-height`, not `height`: a laid-out subtree in there wraps and
    // grows the rail past the bottom of the card it is docked to.
    content?.toggleAttribute("hidden", !open);
    toggle.setAttribute("aria-expanded", String(open));
    const label = open
        ? t("js.toolrating.collapsePanel", "Collapse panel")
        : t("js.toolrating.expandPanel", "Expand panel");
    toggle.title = label;
    toggle.setAttribute("aria-label", label);
    const icon = query("[data-tn-rail-toggle-icon]", toggle);
    if (icon !== null) {
        setIcon(icon, open ? "right_panel_close" : "right_panel_open");
    }
}
function initRail() {
    const rail = query("[data-tn-rail]");
    const toggle = query("[data-tn-rail-toggle]");
    if (rail === null || toggle === null)
        return;
    if (rail.dataset["tnRailBound"] === "1")
        return;
    rail.dataset["tnRailBound"] = "1";
    const content = byId("tool-rail-content");
    let open = false;
    applyRailState(rail, toggle, content, open);
    const setOpen = (next) => {
        if (next === open)
            return;
        open = next;
        applyRailState(rail, toggle, content, open);
        if (open) {
            // One-way drain of a retired preference. AppState.railCollapsed used to
            // pin the rail to its icon strip, which the resting rail already does — so
            // `.is-collapsed` never named a distinct state and its rules are gone
            // (tool-public-editorial.css). The stored flag outlives them in real
            // browsers, so an explicit open normalizes it: if the rail ever regains a
            // collapse affordance, a `true` left over from the old meaning cannot
            // resurface as the new one.
            if (AppState.railCollapsed.get())
                AppState.railCollapsed.set(false);
            return;
        }
        // Closing while focus sits on a rail control keeps :focus-within true, which
        // holds the mobile bottom sheet at full height around an empty panel and
        // makes Escape look dead. Return focus to the disclosure so keyboard users
        // keep their place instead of losing focus to body.
        const focused = document.activeElement;
        if (focused instanceof HTMLElement && rail.contains(focused) && focused !== toggle) {
            toggle.focus();
        }
    };
    toggle.addEventListener("click", () => setOpen(!open));
    // The four tab icons are the rail's other entry point: choosing a panel is a
    // request to see it. One delegated listener on the rail, so it survives markup
    // the page module re-renders inside; it never calls preventDefault, so that
    // module's own [data-action] handler still switches the tab.
    rail.addEventListener("click", (event) => {
        const target = event.target;
        if (!(target instanceof Element))
            return;
        if (target.closest("[data-tn-rail-toggle]") !== null)
            return;
        if (target.closest('[data-action="switch-rail-tab"]') !== null)
            setOpen(true);
    });
    closeRail = () => setOpen(false);
}
function init() {
    initRatingWidget();
    initRail();
}
if (typeof window !== "undefined") {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init, { once: true });
    }
    else {
        init();
    }
}
//# sourceMappingURL=tool-rating.js.map